import { FileText, AlertTriangle } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { modulisticaAPI } from '../../services/modulisticaApi';
import { apriFile } from '../../services/fileUrl';
import RicercaDocumenti from './RicercaDocumenti';
import '../../styles/DocumentiTab.css';
import { dataOraIta } from '../../utils/oraItaliana';

/**
 * DocumentiTab
 * Elenco dei documenti archiviati per il paziente: moduli firmati,
 * consensi, referti caricati.
 *
 * Props:
 *  - pazienteId
 */

const ETICHETTE_TIPO = {
    anagrafica: { testo: 'Anagrafica', classe: 'tipo-anagrafica' },
    privacy: { testo: 'Privacy', classe: 'tipo-privacy' },
    foto: { testo: 'Consenso foto', classe: 'tipo-foto' },
    fea: { testo: 'Consenso FEA', classe: 'tipo-fea' },
    prestazione: { testo: 'Consenso procedura', classe: 'tipo-prestazione' },
    attestazione: { testo: 'Attestazione', classe: 'tipo-attestazione' },
    altro: { testo: 'Altro', classe: 'tipo-altro' }
};

const ETICHETTE_FIRMA = {
    semplice: 'Firma elettronica semplice',
    fea: 'Firma elettronica avanzata',
    cartacea: 'Firma cartacea'
};

const formattaDimensione = (byte) => {
    if (!byte) return '-';
    if (byte < 1024) return byte + ' B';
    if (byte < 1024 * 1024) return (byte / 1024).toFixed(0) + ' KB';
    return (byte / 1024 / 1024).toFixed(1) + ' MB';
};

/* Stessa fonte del piede dei PDF (`utils/oraItaliana.js`): fino al 23
   settembre 2026 qui e sul server c'era la stessa espressione senza
   `timeZone`, e davano due ore diverse - il browser l'ora italiana, il
   server quella UTC. Chi confrontava la schermata col PDF firmato
   trovava due ore. */
const formattaData = (valore) => (valore ? dataOraIta(valore) : '-');

const DocumentiTab = ({ pazienteId }) => {
    const navigate = useNavigate();
    /* I cicli di QUESTO paziente aperti su una versione di consenso
       superata. La domanda qui non e' «chi va reinformato» ma «questo
       qui va reinformato?», e la risposta va data dove si guardano i
       suoi consensi - non in una schermata che bisogna sapere che
       esiste. Fino al 19 settembre 2026 quella schermata non aveva
       nessuna porta. */
    const [daReinformare, setDaReinformare] = useState([]);
    // '' vale per tutti i tipi
    const [filtroTipo, setFiltroTipo] = useState('');
    const [documenti, setDocumenti] = useState([]);
    const [caricamento, setCaricamento] = useState(true);
    const [errore, setErrore] = useState(null);
    const [hashVisibile, setHashVisibile] = useState(null);

    useEffect(() => {
        let attivo = true;

        const carica = async () => {
            try {
                setCaricamento(true);
                setErrore(null);
                const risposta = await modulisticaAPI.getDocumenti(pazienteId);
                if (attivo) setDocumenti(risposta.data.data.documenti || []);
            } catch (e) {
                console.error('Errore caricamento documenti:', e);
                if (attivo) setErrore(e?.message || 'Impossibile caricare i documenti');
            } finally {
                if (attivo) setCaricamento(false);
            }
        };

        if (pazienteId) carica();
        return () => { attivo = false; };
    }, [pazienteId]);

    useEffect(() => {
        if (!pazienteId) return undefined;
        let attivo = true;
        api.get('/versioni-consenso/cicli',
                { params: { paziente_id: pazienteId, solo_da_fare: 'true' } })
            .then((r) => { if (attivo) setDaReinformare(r.data?.data?.cicli || []); })
            .catch(() => { /* l'avviso semplicemente non compare */ });
        return () => { attivo = false; };
    }, [pazienteId]);

    /* Un avviso che si disegna prima di tutto il resto, compreso il
       caso «nessun documento»: un paziente puo' avere un ciclo aperto e
       nessun documento archiviato, e l'avviso serve comunque. */
    const avvisoReinformare = daReinformare.length > 0 ? (
        <div className="doc-reinformare">
            <AlertTriangle size={18} aria-hidden="true" />
            <div>
                <b>
                    {daReinformare.length === 1
                        ? 'Un ciclo di sedute prosegue'
                        : `${daReinformare.length} cicli di sedute proseguono`}
                    {' '}su una versione di consenso precedente.
                </b>
                <p>
                    Il consenso firmato all&apos;inizio è stato rivisto nel
                    frattempo. Non si rifirma: va detto al paziente alla
                    prossima seduta, e annotato.
                </p>
                <button type="button" onClick={() => navigate('/consensi-da-reinformare')}>
                    Apri i consensi da reinformare
                </button>
            </div>
        </div>
    ) : null;

    if (caricamento) {
        return <div className="doc-stato">Caricamento documenti…</div>;
    }

    if (errore) {
        return (
            <div className="doc-stato doc-stato-errore">
                {errore}
            </div>
        );
    }

    if (documenti.length === 0) {
        return (
            <>
            {avvisoReinformare}
            <div className="doc-vuoto">
                <div className="doc-vuoto-icona"><FileText size={38} strokeWidth={1.2} aria-hidden="true" /></div>
                <h3>Nessun documento archiviato</h3>
                <p>I moduli compilati e firmati dal paziente compariranno qui.</p>
            </div>
            </>
        );
    }

    /* Filtro per tipologia: con il tempo i documenti di un paziente
       diventano molti, e cercarne uno a occhio non funziona. */
    const documentiVisibili = filtroTipo
        ? documenti.filter((d) => d.tipo === filtroTipo)
        : documenti;

    // Solo i tipi presenti: mostrare filtri che non danno risultati
    // e' peggio che non averli.
    const tipiPresenti = [...new Set(documenti.map((d) => d.tipo))]
        .filter((x) => ETICHETTE_TIPO[x]);

    return (
        <div className="doc-contenitore">
            {avvisoReinformare}
            <div className="doc-intestazione">
                <h3>Documenti archiviati</h3>
                <span className="doc-conteggio">{documentiVisibili.length}</span>
            </div>

            {/* La ricerca nel testo: sta qui, dove uno guarda i documenti
                e si chiede «in quale c'era scritto...». */}
            <RicercaDocumenti pazienteId={pazienteId} />

            {tipiPresenti.length > 1 && (
                <div className="doc-filtri">
                    <button className={filtroTipo === '' ? 'attivo' : ''}
                            onClick={() => setFiltroTipo('')}>
                        Tutti
                        <span className="conteggio">{documenti.length}</span>
                    </button>
                    {tipiPresenti.map((x) => (
                        <button key={x} className={filtroTipo === x ? 'attivo' : ''}
                                onClick={() => setFiltroTipo(x)}>
                            {ETICHETTE_TIPO[x].testo}
                            <span className="conteggio">
                                {documenti.filter((d) => d.tipo === x).length}
                            </span>
                        </button>
                    ))}
                </div>
            )}

            <table className="doc-tabella">
                <thead>
                    <tr>
                        <th>Tipo</th>
                        <th>Documento</th>
                        <th>Firmato il</th>
                        <th>Firma</th>
                        <th>Dimensione</th>
                        <th>Azioni</th>
                    </tr>
                </thead>
                <tbody>
                    {documentiVisibili.map((d) => {
                        const tipo = ETICHETTE_TIPO[d.tipo] || ETICHETTE_TIPO.altro;
                        return (
                            <React.Fragment key={d.id}>
                                <tr>
                                    <td>
                                        <span className={`doc-badge ${tipo.classe}`}>{tipo.testo}</span>
                                    </td>
                                    <td className="doc-titolo">{d.titolo || 'Documento'}</td>
                                    <td>{formattaData(d.firmato_il || d.created_at)}</td>
                                    <td className="doc-firma">{ETICHETTE_FIRMA[d.firma_tipo] || '-'}</td>
                                    <td>{formattaDimensione(d.dimensione_byte)}</td>
                                    <td>
                                        <div className="doc-azioni">
                                            <button
                                                type="button"
                                                onClick={() => apriFile(d.url)}
                                                className="doc-btn"
                                                title="Apri il documento in una nuova scheda"
                                            >
                                                Apri
                                            </button>
                                            <button
                                                type="button"
                                                className="doc-btn doc-btn-chiaro"
                                                onClick={() => setHashVisibile(hashVisibile === d.id ? null : d.id)}
                                                title="Mostra l'impronta di integrita del file"
                                            >
                                                Integrita
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                                {hashVisibile === d.id && (
                                    <tr className="doc-riga-hash">
                                        <td colSpan="6">
                                            <div className="doc-hash">
                                                <span className="doc-hash-etichetta">SHA-256</span>
                                                <code>{d.hash_sha256}</code>
                                            </div>
                                            <p className="doc-hash-nota">
                                                Impronta calcolata al momento dell'archiviazione. Se il file venisse
                                                modificato, l'impronta ricalcolata risulterebbe diversa.
                                            </p>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default DocumentiTab;
